import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getUserData,
  updateUserData,
  getUserPurchaseHistory,
  //   getUserToken,
  //   updateUserToken,
  deleteUser,
  addUserAddress,
  passwordReset,
  //   getAdminToken,
} from "../../services/user";
import toast from "react-hot-toast";
import { useAuth } from "../../utils/AuthContext";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

function useUser() {
  const queryClient = useQueryClient();
  const { setIsLoggedIn, setUser, isLoggedIn } = useAuth();
  const navigate = useHistory();

  // Get user profile
  const {
    data: userProfile,
    isLoading: isLoadingProfile,
    error: profileError,
    refetch: refetchProfile,
  } = useQuery({
    queryKey: ["userProfile"],
    queryFn: getUserData,
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!isLoggedIn,
  });

  // Get user purchase history
  const {
    data: purchaseHistory,
    isLoading: isLoadingHistory,
    error: historyError,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: ["userPurchaseHistory"],
    queryFn: getUserPurchaseHistory,
    retry: 2,
    staleTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!isLoggedIn,
  });

  // Get user FCM token
  //   const {
  //     data: userToken,
  //     isLoading: isLoadingToken,
  //     error: tokenError,
  //     refetch: refetchToken,
  //   } = useQuery({
  //     queryKey: ["userToken"],
  //     queryFn: () => getUserToken(),
  //     retry: 2,
  //     staleTime: 30 * 60 * 1000, // 30 minutes
  //   });

  // Get admin FCM token
  //   const {
  //     data: adminToken,
  //     isLoading: isLoadingAdminToken,
  //     error: adminTokenError,
  //     refetch: refetchAdminToken,
  //   } = useQuery({
  //     queryKey: ["adminToken"],
  //     queryFn: getAdminToken,
  //     retry: 2,
  //     staleTime: 30 * 60 * 1000, // 30 minutes
  //     enabled: userProfile?.role === 1, // Only fetch if user is admin
  //   });

  // Update user profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: updateUserData,
    onSuccess: (data) => {
      queryClient.setQueryData(["userProfile"], data);
      setUser(data);
      toast.success("Profile updated successfully");
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.error || "Failed to update profile";
      toast.error(errorMessage);
    },
  });

  // Update FCM token mutation
  //   const updateTokenMutation = useMutation({
  //     mutationFn: updateUserToken,
  //     onSuccess: () => {
  //       queryClient.invalidateQueries(["userToken"]);
  //       toast.success("FCM token updated successfully");
  //     },
  //     onError: (error) => {
  //       const errorMessage =
  //         error?.response?.data?.error || "Failed to update FCM token";
  //       toast.error(errorMessage);
  //     },
  //   });

  // Delete user account mutation
  const deleteUserMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      setIsLoggedIn(false);
      setUser(null);
      localStorage.removeItem("userData");
      localStorage.removeItem("auth_token");
      queryClient.clear();
      toast.success("Account deleted successfully");
      navigate.push("/");
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.error || "Failed to delete account";
      toast.error(errorMessage);
    },
  });

  // Add/update user address mutation
  const updateAddressMutation = useMutation({
    mutationFn: addUserAddress,
    onSuccess: (data) => {
      queryClient.setQueryData(["userProfile"], data);
      setUser(data);
      toast.success("Address updated successfully");
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.error || "Failed to update address";
      toast.error(errorMessage);
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: ({ currentPassword, newPassword }) =>
      passwordReset(currentPassword, newPassword),
    onSuccess: () => {
      toast.success("Password updated successfully");
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.error || "Failed to update password";
      toast.error(errorMessage);
    },
  });

  // Convenience functions
  const updateProfile = (data) => {
    updateProfileMutation.mutate(data);
  };

  const updatePasswordMutation = (data) => {};

  //   const updateFCMToken = (token) => {
  //     updateTokenMutation.mutate({ token });
  //   };

  const deleteAccount = () => {
    if (
      window.confirm(
        "Are you sure you want to delete your account? This action cannot be undone."
      )
    ) {
      deleteUserMutation.mutate();
    }
  };

  const updateAddress = (address) => {
    updateAddressMutation.mutate({ address });
  };

  const resetPassword = (data) => {
    resetPasswordMutation.mutate(data);
  };

  // Loading states
  const isLoading = isLoadingProfile || isLoadingHistory;
  const isUpdating =
    updateProfileMutation.isLoading ||
    // updateTokenMutation.isLoading ||
    deleteUserMutation.isLoading ||
    updateAddressMutation.isLoading;

  // Error states
  const hasError = profileError || historyError;

  return {
    // Data
    userProfile,
    purchaseHistory,
    // userToken,
    // adminToken,

    // Loading states
    isLoading,
    isLoadingProfile,
    isLoadingHistory,
    // isLoadingToken,
    // isLoadingAdminToken,
    isUpdating,

    // Error states
    hasError,
    profileError,
    historyError,
    // tokenError,
    // adminTokenError,

    // Mutations
    updateProfile,
    // updateFCMToken,
    deleteAccount,
    updateAddress,
    resetPassword,

    // Refetch functions
    refetchProfile,
    refetchHistory,
    // refetchToken,
    // refetchAdminToken,

    // Mutation states
    isUpdatingProfile: updateProfileMutation.isLoading,
    // isUpdatingToken: updateTokenMutation.isLoading,
    isDeletingAccount: deleteUserMutation.isLoading,
    isUpdatingAddress: updateAddressMutation.isLoading,
    isResettingPassword: resetPasswordMutation.isLoading,
    resetPasswordError: resetPasswordMutation.error,
  };
}

export default useUser;
